import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../styles/ResetPassword.css";
import logo from "../assets/logo1.png";
import 'bootstrap-icons/font/bootstrap-icons.css';
import Navbar from '../components/Navbar';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [passwordReset, setPasswordReset] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setMessage({ type: "error", text: "Token de recuperación no válido" });
      setVerifying(false);
      return;
    }

    setToken(tokenFromUrl);
    verifyToken(tokenFromUrl);
  }, [searchParams]);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/verify-reset-token/${tokenToVerify}`
      );
      
      setEmail(response.data.email);
      setMessage({ type: "success", text: "Token válido. Puedes restablecer tu contraseña." });
    } catch (error) {
      setMessage({ 
        type: "error", 
        text: error.response?.data?.message || "Token inválido o expirado" 
      });
    } finally {
      setVerifying(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validatePassword = (password) => {
    if (password.length < 6) {
      return "La contraseña debe tener al menos 6 caracteres";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    // Validaciones
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setMessage({ type: "error", text: passwordError });
      setLoading(false);
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage({ type: "error", text: "Las contraseñas no coinciden" });
      setLoading(false);
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/reset-password`,
        {
          token,
          newPassword: formData.newPassword
        }
      );

      setMessage({ 
        type: "success", 
        text: "Contraseña actualizada exitosamente" 
      });
      setPasswordReset(true);
    } catch (error) {
      setMessage({ 
        type: "error", 
        text: error.response?.data?.message || "Error al actualizar la contraseña" 
      });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <>
        <Navbar />
        <div className="reset-password-container">
          <div className="reset-password-card">
            <div className="verifying-state">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Verificando...</span>
              </div>
              <h3>Verificando token...</h3>
              <p>Por favor espera mientras verificamos tu enlace de recuperación</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="reset-password-container">
        <div className="reset-password-card">
          <div className="reset-password-header">
            <img src={logo} alt="Logo" className="reset-password-logo" />
            <h2>Restablecer Contraseña</h2>
            {email && (
              <p>Restablece la contraseña para <strong>{email}</strong></p>
            )}
          </div>

          {!passwordReset ? (
            <form onSubmit={handleSubmit} className="reset-password-form">
              <div className="form-group">
                <label htmlFor="newPassword" className="form-label">
                  <i className="bi bi-lock"></i> Nueva Contraseña
                </label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  className="form-control"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Ingresa tu nueva contraseña"
                  required
                  disabled={loading}
                  minLength="6"
                />
                <small className="form-text">
                  Mínimo 6 caracteres
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">
                  <i className="bi bi-lock-fill"></i> Confirmar Contraseña
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  className="form-control"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirma tu nueva contraseña"
                  required
                  disabled={loading}
                />
              </div>

              {message.text && (
                <div className={`alert ${message.type === "success" ? "alert-success" : "alert-danger"}`}>
                  <i className={`bi ${message.type === "success" ? "bi-check-circle" : "bi-exclamation-triangle"}`}></i>
                  {message.text}
                </div>
              )}

              <button 
                type="submit" 
                className="btn btn-primary reset-password-btn"
                disabled={loading || !token}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Actualizando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-shield-check"></i> Actualizar Contraseña
                  </>
                )}
              </button>

              <div className="reset-password-links">
                <Link to="/login" className="back-to-login">
                  <i className="bi bi-arrow-left"></i> Volver al Login
                </Link>
              </div>
            </form>
          ) : (
            <div className="password-reset-success">
              <div className="success-icon">
                <i className="bi bi-check-circle-fill"></i>
              </div>
              <h3>¡Contraseña Actualizada!</h3>
              <p>
                Tu contraseña ha sido actualizada exitosamente.
              </p>
              <p className="instructions">
                Ya puedes iniciar sesión con tu nueva contraseña.
              </p>
              <div className="success-actions">
                <Link to="/login" className="btn btn-primary">
                  <i className="bi bi-box-arrow-in-right"></i> Iniciar Sesión
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
